import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    chain,
    get,
    isEmpty,
    isFunction,
    isString,
    map,
    merge,
    noop,
    orderBy,
    slice,
    toLower,
    trim,
    values,
} from 'lodash';
import { t } from '@lingui/macro';

import { HTTP_STATUS_403, useDataService } from '../../services/ApiDataService';
import { useFilterState } from '../../stores/FilterStateStore';
import { useIsGlobal } from '../../utilities/PgmHooks';
import useAlerts from '../../stores/AlertsStore';
import { getLogger } from '../../utilities/UseLog';
import { useNetTestNowIsLoading } from '../pages/net-test-now-page/NetTestNowIsLoadingContext';

const log = getLogger('Loader');

export const SERVER_ERROR_MESSAGE = t`An error occurred on the server.  If this issue persists, please contact support.`;
export const NOT_AUTHORIZED_MESSAGE = t`You do not have access to this resource.  Please contact your system administrator for access.`;

export function convertLoadParamsToQueryParams(loadParams, enableCursor) {
    const { filters, sorters, limit, start, cursor } = loadParams;
    let offset = {};
    if (enableCursor) {
        offset = cursor ? { cursor } : {};
    } else {
        offset = { rowStart: start };
    }

    const params = {
        rowLimit: limit,
        ...offset,
    };

    if (!isEmpty(sorters)) {
        params.sort = sorters
            ?.map(({ property, direction }) => {
                const dir = direction === 'desc' ? '-' : '';
                return property
                    .split(',')
                    .map((prop) => `${dir}${prop}`)
                    .join(',');
            })
            .join(',');
    }

    if (!isEmpty(filters)) {
        params.search = filters.map((f) => trim(f.value));
    }

    return params;
}

/**
 * Returns noop on first render, then returns the actual query loader on subsequent renders.
 * This prevents double-loading when both DataTable and FilterContainer trigger loader execution.
 *
 * Used for infrastructure list pages where FilterContainer initializes filters after DataTable mounts.
 *
 * @param {{url: string, crudUrl: string, totalProperty: string, dataProperty: string, params: {searchFields}}} dataSource
 * @param options
 * @param firstTime - If true, returns noop; if false, returns the actual loader
 * @returns {(loaderParams: any) => Promise<T>}
 */
export function useFilterQueryLoader(dataSource, options = {}, firstTime = false) {
    const loader = useQueryLoader(dataSource, options);
    if (firstTime) {
        return noop;
    }

    return loader;
}

/**
 * Custom hook that manages the first-time loading state and returns a filter-aware query loader.
 * This hook encapsulates the common pattern used in infrastructure list pages to prevent
 * double-loading when both DataTable and FilterContainer trigger loader execution.
 *
 * Pattern:
 * 1. First render: returns noop to skip DataTable's initial load
 * 2. After mount: sets firstTime to false, allowing FilterContainer to trigger the actual load
 *
 * @param {{url: string, crudUrl: string, totalProperty: string, dataProperty: string, params: {searchFields}}} dataSource
 * @param {{disableFilterState?: boolean}} options
 * @returns {(loaderParams: any) => Promise<T>} The loader function
 *
 * @example
 * // In your infrastructure list component:
 * const loader = useFilterContainerLoader(VIRTUAL_INFRASTRUCTURE_DATA_SOURCE, QUERY_LOADER_OPTION);
 */
export function useFilterContainerLoader(dataSource, options = {}) {
    const [firstTime, setFirstTime] = useState(true);

    useEffect(() => {
        setFirstTime(false);
    }, []);

    return useFilterQueryLoader(dataSource, options, firstTime);
}

/**
 * @template T
 *
 * Returns a loader function for a datatable
 *
 * @param {{url: string, crudUrl: string, totalProperty: string, dataProperty: string, params: {searchFields}}} dataSource
 * @param options
 * @returns {(loaderParams: any) => Promise<T>}
 */
export function useQueryLoader(dataSource, options = {}) {
    const { disableFilterState = false } = options;
    const [realFilterState] = useFilterState();
    const filterState = disableFilterState ? null : realFilterState;

    const dataService = useDataService();
    const isGlobal = useIsGlobal();
    const [, /* Not used */ alertsActions] = useAlerts();

    return useMemo(() => {
        if (!dataSource || (!disableFilterState && !filterState)) {
            return null;
        }

        return async (loaderParams) => {
            const enableCursor = isGlobal;
            const { url, params: datasourceParams, overrideParams } = dataSource;
            const queryParams = mergeLoadParams(
                filterState,
                datasourceParams,
                loaderParams,
                overrideParams,
                enableCursor
            );

            try {
                const response = await dataService.get(url, queryParams);
                return convertQueryResponse(dataSource, response);
            } catch (error) {
                log.error(error);

                if (error?.response?.status === HTTP_STATUS_403) {
                    alertsActions.createErrorAlert(NOT_AUTHORIZED_MESSAGE);
                } else {
                    alertsActions.createErrorAlert(SERVER_ERROR_MESSAGE);
                }
                return convertQueryResponse(dataSource, null);
            }
        };
    }, [disableFilterState, filterState, dataSource, dataService, isGlobal, alertsActions]);
}

export function useQueryNowLoader(dataSource) {
    const [, setNetTestNowIsLoading] = useNetTestNowIsLoading();
    const [filterState] = useFilterState();
    const dataService = useDataService();
    const isGlobal = useIsGlobal();
    const [, /* Not used */ alertsActions] = useAlerts();

    return useMemo(() => {
        if (!dataSource) {
            // you must have a datasource for the query loader to work...
            // the net-test-now test doesn't have a filterState
            return null;
        }

        return async (loaderParams) => {
            const enableCursor = isGlobal;
            const {
                url,
                params: datasourceParams,
                overrideParams,
                numericMetrics,
                isResultsPage,
                metricsToNotSort,
            } = dataSource;

            const queryParams = mergeLoadParams(
                filterState,
                datasourceParams,
                loaderParams,
                overrideParams,
                enableCursor
            );

            try {
                const sort = queryParams?.sort;
                const response = await dataService.get(url, queryParams);

                // The NetTestNow test list panel needs the service tests names added to it.
                const testResponse = !isResultsPage ? await dataService.get('/api/v3/service-tests/list') : null;
                // The NetTestNow results panel needs the nPoint names added to it.
                const agentResponse = isResultsPage ? await dataService.get('/api/v3/npoints/list') : null;

                if (!isResultsPage) {
                    // add the existing test name to the results
                    map(response.data, (testRun) => {
                        map(testResponse.data, (existingTest) => {
                            if (testRun.existingTestId === existingTest.id)
                                testRun.existingTestName = existingTest.name;
                        });
                    });

                    // add the nPoint count to each testRun result
                    response.data.map((testRun) => (testRun.numAgents = testRun.agents?.ids?.length));
                } else {
                    // The quicktest itself can timeout and 'error' but the nPoints remain in the 'initializing' state and never get a result.
                    const didError = response?.status === 'Error';
                    // Get the IDs of all the nPoints that never got a result by the time the test itself timed out / errored.
                    const whichAgentsErrored = didError
                        ? response?.agentResults.filter((agentResult) => !agentResult.resultCount)
                        : [];

                    // Make a new array of objects that includes the first result from each nPoints respective 'result' array. This array should only have one result now that scriptedQuicktests do not run 5 times like the webQuickTests.
                    const results = map(response?.agentResults, (result) => {
                        const resultData = result?.results?.shift();
                        return resultData?.data;
                    });

                    // The errored nPoints have 'undefined' results -- just remove them so that we can put in a manually created 'error' result
                    // we cant do this before the test errors or the 'loading...' bars will not show up when they should. ie* the test result panel will initially be completely blank when no results have returned yet.
                    const filteredResults = didError ? results.filter((result) => result !== undefined) : results;

                    // After removing the undefined results - add a new result to the results array with 'Error' Values and the ID of the errored nPoint.
                    if (didError) {
                        whichAgentsErrored?.map((erroredAgent) => {
                            return filteredResults.push({
                                agentId: erroredAgent.agent,
                                message: 'Error',
                                'measurement-status': 'Error',
                            });
                        });
                    }

                    // Neither the agentResults object, nor each agent's actual results object includes the name of the nPoint. But since they include the nPoint ID, the name can be added to the object by matching IDs from the seperate npoints/list query.
                    // Name has to be put into the data object so that everything is in the same place and data doesn't come from several levels.
                    map(filteredResults, (result) => {
                        map(agentResponse.data, (agent) => {
                            if (result?.agentId === agent.id) {
                                result.name = agent.name;
                            }
                        });
                    });

                    response.resultLength = filteredResults.length;
                    response.data = filteredResults;
                }

                const sorter = (sortKey, unsortedResults) => {
                    const descending = sortKey[0] === '-';
                    const key = descending ? sortKey.substring(1) : sortKey;

                    // If these non-string values are sorted the same as the rest it could be wrong. eg* 25 would be bigger than 100 because 2 > 1
                    if (numericMetrics?.includes(key)) {
                        if (descending) {
                            return [...unsortedResults].sort((a, b) => a[key] - b[key]);
                        }
                        return [...unsortedResults].sort((a, b) => b[key] - a[key]);
                    }
                    // When the timestamp is sorted the same as below, it leaves out the most recent ones for some reason
                    if (metricsToNotSort?.includes(key)) return unsortedResults;

                    if (descending) {
                        return [...unsortedResults].sort((a, b) => (a[key] > b[key] ? 1 : -1));
                    }
                    return [...unsortedResults].sort((a, b) => (a[key] < b[key] ? 1 : -1));
                };

                // if result data was missing, the sorter function would error trying to use 'undefined' as an iterable.
                if (response.data && sort) {
                    const sortedResults = sorter(sort, response.data);
                    response.data = sortedResults;
                }

                response.count = response.resultLength || response.count;

                if (response?.status?.toLowerCase() !== 'initializing') {
                    setNetTestNowIsLoading(false);
                } else {
                    setNetTestNowIsLoading(true);
                }

                return convertQueryResponse(dataSource, response);
            } catch (error) {
                log.error(error);

                if (error?.response?.status === HTTP_STATUS_403) {
                    alertsActions.createErrorAlert(NOT_AUTHORIZED_MESSAGE);
                } else {
                    alertsActions.createErrorAlert(SERVER_ERROR_MESSAGE);
                }
                return convertQueryResponse(dataSource, null);
            }
        };
    }, [filterState, dataSource, dataService, isGlobal, alertsActions, setNetTestNowIsLoading]);
}

export function useQueryPostLoader(dataSource, selected, isInverted) {
    const dataService = useDataService();
    const [, /* Not used */ alertsActions] = useAlerts();

    return useMemo(() => {
        if (!dataSource) {
            return null;
        }

        return (loaderParams) => {
            const { url, params: datasourceParams, body } = dataSource;
            const queryParams = merge({}, datasourceParams, convertLoadParamsToQueryParams(loaderParams));

            return dataService
                .post(
                    url,
                    {
                        selection: selected,
                        inverted: isInverted,
                        ...(body || {}),
                    },
                    queryParams
                )
                .then((response) => convertQueryResponse(dataSource, response))
                .catch((error) => {
                    log.error(error);

                    alertsActions.createErrorAlert(SERVER_ERROR_MESSAGE);

                    return convertQueryResponse(dataSource, null);
                });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataService, dataSource, selected]);
}

export function convertQueryResponse(dataSource, response) {
    const totalProperty = dataSource?.totalProperty || 'total';
    // countOf summary info
    const countOfOnProperty = dataSource?.countOfOnProperty || 'countOfOn';
    const countOfOffProperty = dataSource?.countOfOffProperty || 'countOfOff';
    const countOfEnabledProperty = dataSource?.countOfEnabledProperty || 'countOfEnabled';
    const countOfDisabledProperty = dataSource?.countOfDisabledProperty || 'countOfDisabled';
    const dataProperty = dataSource?.dataProperty || 'data';
    return {
        cursor: response?.cursor,
        length: get(response, totalProperty),
        countOfOn: get(response, countOfOnProperty, -1),
        countOfOff: get(response, countOfOffProperty, -1),
        countOfEnabled: get(response, countOfEnabledProperty, -1),
        countOfDisabled: get(response, countOfDisabledProperty, -1),
        rows: get(response, dataProperty),
    };
}

export function mergeLoadParams(filterState, dataSourceParams, loaderParams, overrideParams, enableCursor) {
    const filterParams = values(filterState?.parameters || {});
    const loadParams = convertLoadParamsToQueryParams(loaderParams, enableCursor);

    return merge({}, dataSourceParams, loadParams, ...filterParams, overrideParams || {});
}

export function useArrayLoader(dataArr, filterCallback, sortCallback = arraySorter) {
    return useCallback(
        ({ start, limit, filters, sorters }) => {
            let rows = dataArr;

            if (!isEmpty(sorters) && isFunction(sortCallback)) {
                rows = sortCallback(dataArr, sorters);
            }

            if (!isEmpty(filters) && isFunction(filterCallback)) {
                rows = filterCallback(rows, filters);
            }

            return Promise.resolve({
                length: rows?.length || 0,
                rows: slice(rows, start, start + limit + 1),
            });
        },
        [dataArr, filterCallback, sortCallback]
    );
}

export function arraySorter(dataArr, sorters) {
    const sortPropertyField = 'property';
    const dirs = map(sorters, (s) => s.direction);

    const props = chain(sorters)
        .map(sortPropertyField)
        .map((prop) => (obj) => {
            const value = get(obj, prop, '');
            if (isString(value)) {
                return toLower(get(obj, prop, ''));
            }

            return value;
        })
        .value();

    return orderBy(dataArr, props, dirs);
}
